import { useState, useEffect, useRef } from 'react'
import Bugsnag from '@bugsnag/js'
import { Formik, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { useMutation, useQueryClient } from 'react-query'

import { Box, Typography, Icon } from '@mui/material'

import AdxInput from 'components/custom/AdxInput'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxDatePicker from 'components/custom/AdxDatePicker'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'

import { handleValidationErrors, handleScroll } from 'services/helper/handleValidationErrors'
import { _calculateAge, _convertAPIDate, _ICBirthdateValidation } from 'services/helper/DateTime'
import { postPersonalData } from 'services/api/modules/staff-management/staffManagementDashboard'
import AdxButton from 'components/custom/AdxButton'

const validationSchema = Yup.object({
  name: Yup.string()
    .nullable()
    .required('Employee Name is required')
    .matches(/^[a-zA-Z-@ /.']+$/, "Name must be valid eg. Alphabet with @, ', -, . and /"),
  nationality: Yup.string().nullable().required('Nationality is required'),
  gender: Yup.number().nullable().required('Gender is required'),
  disability_status: Yup.string().nullable().required('Disability Status is required'),
  dob: Yup.string().nullable().required('Date of Birth is required'),
  id_type: Yup.string().nullable().required('Identification Type is required'),
  id_number: Yup.string()
    .nullable()
    .when('id_type', {
      is: 'nric',
      then: Yup.string()
        .nullable()
        .required('IC Number is required')
        .matches(/^[0-9]{12}?$/, 'IC Number must be valid, eg. 880208104178'),
      otherwise: Yup.string()
        .nullable()
        .required('Passport Number is required')
        .matches(/^[A-Z\d]+$/i, 'Passport Number must be alphanumeric, eg. A32127654'),
    }),
})

const BasicDetails = ({ data, windowCheckEmployeeFile, employeeID, countriesList }) => {
  const queryClient = useQueryClient()
  const formikRef = useRef()

  // Check birth year of 18 years old
  const legalAge = new Date().getFullYear() - 18
  // Check birth year of 60 years old
  const retiredAge = new Date().getFullYear() - 70
  const [disable, setDisable] = useState(true)
  const [initialValues, setInitialValues] = useState({
    name: '',
    gender: '',
    nationality: '',
    disability_status: 0,
    dob: '',
    id_type: '',
    id_number: '',
  })

  const mutation = useMutation((value) => postPersonalData(data?.data?.id, value), {
    onSuccess: () => {
      queryClient.invalidateQueries(['detailedEmployeeData', employeeID])
      queryClient.invalidateQueries(['employeePersonalData', employeeID])
      setDisable(true)
    },
    onError: (error) => {
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        if (error.data.errors) {
          handleScroll(error.data.errors)
        }
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const handlePostData = (values) => {
    mutation.mutate(values)
  }

  const handleSubmitBasic = async (values, action) => {
    let newValues = {
      ...values,
      dob: _convertAPIDate(values.dob),
    }

    let isMatched = _ICBirthdateValidation(newValues)

    if (newValues.id_type === 'nric') {
      if (isMatched) {
        handlePostData(newValues)
      } else {
        let errors = {
          dob: ['Date of Birth in ID number must be matched'],
          id_number: ['Date of Birth in ID number must be matched'],
        }
        let newError = {
          data: {
            errors,
          },
        }
        handleValidationErrors(newError, formikRef)
      }
    } else {
      handlePostData(newValues)
    }
  }

  const cancelEditForm = (resetForm) => {
    resetForm()
    setDisable(true)
  }

  useEffect(() => {
    if (data) {
      setInitialValues({
        name: data?.data?.name,
        gender: data?.data?.gender,
        nationality: data?.data?.nationaliy ? data?.data?.nationaliy : 'Malaysia',
        disability_status: data?.data?.disability_status,
        dob: data?.data?.dob,
        id_type: data?.data?.id_type,
        id_number: data?.data?.id_number ? data?.data?.id_number : '',
      })
    }
  }, [data])

  const handleCheckRef = async (handleFormikSubmit) => {
    await handleFormikSubmit()
    const { errors = {} } = formikRef.current
    handleScroll(errors)
  }

  return (
    <Box backgroundColor="white.main" borderRadius="4.5px">
      <Formik
        onSubmit={handleSubmitBasic}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        innerRef={formikRef}
      >
        {({ values, touched, errors, handleChange, handleSubmit, setFieldValue, resetForm }) => (
          <Form>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'gray-line.main',
                p: 2,
              }}
              alignItems="center"
            >
              <Typography color="light-gray-text.main" fontWeight={500}>
                Basic details
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
                borderBottom: windowCheckEmployeeFile && '1px solid',
                borderColor: 'gray-line.main',
              }}
            >
              <Box>
                <AdxInput
                  label="Employee Name"
                  disabled={disable}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Employee Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  sx={{ marginBottom: 2.5 }}
                  errorMessage={<ErrorMessage name="name" component={AdxErrorMessage} />}
                  isErrorShown={touched.name && errors.name}
                />
              </Box>

              <Box gap={2} display="flex" alignItems="flex-start">
                {/* Gender */}
                <AdxDropdown
                  placeholder="Gender"
                  label="Gender"
                  disabled={disable}
                  sx={{ width: '40%' }}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxDropdown={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  name="gender"
                  value={values.gender}
                  onChange={handleChange}
                  errorMessage={<ErrorMessage name="gender" component={AdxErrorMessage} />}
                  isErrorShown={touched.gender && errors.gender}
                >
                  <AdxMenuItem value={1} selectedvalue={values.gender}>
                    {'Male'}
                  </AdxMenuItem>
                  <AdxMenuItem value={2} selectedvalue={values.gender}>
                    {'Female'}
                  </AdxMenuItem>
                </AdxDropdown>
                {/* Disability Status */}
                <AdxDropdown
                  placeholder="Disability Status"
                  label="Disability Status"
                  disabled={disable}
                  sx={{ width: '60%' }}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxDropdown={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  name="disability_status"
                  value={values.disability_status}
                  onChange={handleChange}
                  errorMessage={
                    <ErrorMessage name="disability_status" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.disability_status && errors.disability_status}
                >
                  <AdxMenuItem value={1} selectedvalue={values.disability_status}>
                    {'Yes'}
                  </AdxMenuItem>
                  <AdxMenuItem value={0} selectedvalue={values.disability_status}>
                    {'No'}
                  </AdxMenuItem>
                </AdxDropdown>
              </Box>
              <Box gap={2} display="flex" sx={{ alignItems: 'center' }}>
                <Box sx={{ width: '40%' }}>
                  <AdxDatePicker
                    label="Date of Birth"
                    disabled={disable}
                    onChange={(e) => setFieldValue(`dob`, e)}
                    name="dob"
                    value={values.dob}
                    minDate={new Date(retiredAge, 0, 1)}
                    maxDate={new Date(legalAge, new Date().getMonth(), new Date().getDate())}
                    errorMessage={<ErrorMessage name="dob" component={AdxErrorMessage} />}
                    isErrorShown={touched.dob && errors.dob}
                    sxLabel={{
                      color: disable ? '#8a8a8a' : 'dark-text.main',
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    width: '60%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                    justifyContent: 'center',
                    pt: 3,
                  }}
                >
                  <Typography variant="body1" color="light-gray-text.main">
                    {_calculateAge(values.dob)}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" sx={{ alignItems: 'center' }}>
                <AdxDropdown
                  placeholder="Nationality"
                  label="Nationality"
                  disabled={disable}
                  sx={{ width: '100%' }}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxDropdown={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  value={values.nationality}
                  name="nationality"
                  onChange={handleChange}
                  errorMessage={<ErrorMessage name="nationality" component={AdxErrorMessage} />}
                  isErrorShown={touched.nationality && errors.nationality}
                >
                  {countriesList &&
                    countriesList.length !== 0 &&
                    countriesList
                      .filter((cc) => cc.country_code !== 'IL')
                      .map((item) => (
                        <AdxMenuItem value={item.name} selectedvalue={values.nationality}>
                          {item.name}
                        </AdxMenuItem>
                      ))}
                </AdxDropdown>
              </Box>
              <Box gap={2} display="flex" sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                <AdxDropdown
                  placeholder="Identification type"
                  label="Identification type"
                  disabled={disable}
                  sx={{ width: { xs: '100%', sm: '40%' } }}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxDropdown={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  name="id_type"
                  value={values.id_type}
                  onChange={handleChange}
                  errorMessage={<ErrorMessage name="id_type" component={AdxErrorMessage} />}
                  isErrorShown={touched.id_type && errors.id_type}
                >
                  {values?.nationality === 'Malaysia' && (
                    <AdxMenuItem value={'nric'} selectedvalue={values.id_type}>
                      {'NRIC'}
                    </AdxMenuItem>
                  )}
                  <AdxMenuItem value={'passport'} selectedvalue={values.id_type}>
                    {'Passport'}
                  </AdxMenuItem>
                </AdxDropdown>
                <AdxInput
                  label="Identification no."
                  disabled={disable ? true : values.id_type ? false : true}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder={values.id_type === 'nric' ? 'E.g: 880208104178' : 'E.g: A34125172'}
                  name="id_number"
                  value={values.id_number}
                  onChange={handleChange}
                  sx={{ width: { xs: '100%', sm: '60%' } }}
                  errorMessage={<ErrorMessage name="id_number" component={AdxErrorMessage} />}
                  isErrorShown={touched.id_number && errors.id_number}
                />
              </Box>
              {windowCheckEmployeeFile && !disable && (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    columnGap: 1,
                  }}
                >
                  <AdxButton
                    variant={'outlined'}
                    onClick={() => cancelEditForm(resetForm)}
                    sx={{ width: 0.2 }}
                  >
                    Cancel
                  </AdxButton>
                  <AdxButton
                    variant={'contained'}
                    onClick={() => handleCheckRef(handleSubmit)}
                    sx={{ width: 0.2 }}
                  >
                    Save
                  </AdxButton>
                </Box>
              )}
            </Box>
            {windowCheckEmployeeFile && disable && (
              <Box sx={{ p: 0.5 }}>
                <AdxButton
                  variant={'ghost'}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                  onClick={() => setDisable(false)}
                >
                  <Icon sx={{ fontSize: '15px', color: 'alternate.main' }}>edit</Icon>
                  <Typography fontWeight={500} color="alternate.main" ml={1.25}>
                    Edit basic details
                  </Typography>
                </AdxButton>
              </Box>
            )}
          </Form>
        )}
      </Formik>
    </Box>
  )
}

export default BasicDetails
